<template>
  <ul v-if="pages.length" class="pagination" :class="[$config.project, { [align]: align }]">
    <li
      class="prev"
      :class="{ disable: !(page - 1) }"
      data-event="pagination_prev.click"
    >
      <nuxt-link
        v-if="links"
        class="page-link"
        :to="{ query: { ...queryParams || $route.query, page: page - 1 !== 1 && page - 1 ? page - 1 : undefined } }"
      >
        <CaretRight />
      </nuxt-link>
      <span v-else class="page-link" @click="$emit('pagechanged', page - 1 !== 1 ? page - 1 : 1)">
        <CaretRight />
      </span>
    </li>
    <li
      v-for="({ number, separator }, index) in pages"
      :key="index"
      :class="{ 'page-item': number, 'empty': separator }"
      :data-event="`pagination_page_${number}.click`"
    >
      <template v-if="number">
        <nuxt-link
          v-if="links"
          class="page-link"
          :class="{ active: number === page }"
          :to="{ query: { ...queryParams || $route.query, page: number !== 1 ? number : undefined } }"
        >
          {{ number }}
        </nuxt-link>
        <span
          v-else
          class="page-link"
          :class="{ active: number === page }"
          @click="$emit('pagechanged', number !== 1 ? number : 1)"
        >
          {{ number }}
        </span>
      </template>
      <template v-else>
        {{ separator }}
      </template>
    </li>
    <li
      class="next"
      :class="{ disable: !(page + 1 <= Math.ceil(total / perPage)) }"
      data-event="pagination_next.click"
    >
      <nuxt-link
        v-if="links"
        class="page-link"
        :to="{ query: { ...queryParams || $route.query, page: page + 1 <= Math.ceil(total / perPage) ? page + 1 : undefined } }"
      >
        <CaretRight />
      </nuxt-link>
      <span v-else class="page-link" @click="$emit('pagechanged', page + 1 <= Math.ceil(total / perPage) ? page + 1 : 1)">
        <CaretRight />
      </span>
    </li>
  </ul>
</template>

<script>
import CaretRight from '../assets/svg/caret-right.svg?vue-component';

const pagination = (c, m, layout, reduced) => {
  const current = c;
  const last = m;
  const delta = layout === 'mobile' || reduced ? 1 : 2;
  const left = current - delta;
  const right = current + delta + 1;
  const range = [];
  const rangeWithDots = [];
  let l;
  const leftMid = reduced ? -1 : Math.ceil(current / 2);
  const rightMid = reduced ? -1 : Math.ceil((last + current) / 2);
  // eslint-disable-next-line no-plusplus
  for (let i = 1; i <= last; i++) {
    // eslint-disable-next-line no-mixed-operators
    if (i === 1 || i === last || i === leftMid || i === rightMid || i >= left && i < right) {
      range.push(i);
    }
  }
  // eslint-disable-next-line no-restricted-syntax
  for (const i of range) {
    if (l && i - l !== 1) {
      rangeWithDots.push({ separator: '...' });
    }
    rangeWithDots.push({ number: i });
    l = i;
  }
  return rangeWithDots;
};
export default {
  components: { CaretRight },
  props: {
    page: { type: Number, default: 1 },
    total: { type: Number, required: true },
    perPage: { type: Number, required: true },
    queryParams: { type: Object, default: () => {}, required: false },
    align: { type: String, required: false, default: 'left' },
    // eslint-disable-next-line vue/no-boolean-default
    links: { type: Boolean, default: true },
    reduced: Boolean,
  },
  computed: {
    pages() {
      return pagination(this.page, Math.ceil(this.total / this.perPage), this.$store.state.layout, this.reduced);
    },
  },
};
</script>

<style scoped lang="scss">
.pagination {
  margin-top: 25px;
  padding-left: 8px;

  &.center {
    display: flex;
    justify-content: center;
  }

  a:focus {
    box-shadow: none;
  }

  li {
    text-align: center;
    list-style: none;
    padding-left: 0;
    margin-left: 0;
    color: var(--main-light-shade-4);

    &.empty {
      display: flex;
      align-items: center;
    }

    a,
    span {
      width: 44px;
      height: 40px;
      border-radius: 4px;
      color: var(--main-light-shade-4);
      display: flex;
      justify-content: center;
      align-items: center;
      position: relative;
      border: 1px solid transparent;
      text-decoration: none;
      cursor: pointer;

      svg {
        width: 16px;
        height: 16px;

        path {
          stroke: var(--main-light-shade-4);
        }
      }
    }

    &.page-item:hover a:not(.active),
    &.page-item:hover span:not(.active) {
      border: 1px solid var(--main-light-shade-4);
    }

    &.prev a,
    &.next a,
    &.prev span,
    &.next span {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &.next a svg,
    &.next span svg {
      transform: rotate(180deg);
    }

    + li {
      margin-left: 13px;
    }
  }

  &.lms {
    .active {
      background-color: transparent;
      color: var(--main-dark);
      font-weight: 600;
    }
  }

  .active {
    background-color: var(--main-dark-shade-1);
    color: var(--main-light);
  }

  .disable {
    pointer-events: none;
    opacity: 0.3;
  }

  .accent-info .page-item .page-link {
    cursor: pointer;
  }
}

@include bp($bp-mobile) {
  .pagination {
    flex-wrap: wrap;

    li {
      &.page-item:hover a:not(.active),
      &.page-item:hover span:not(.active) {
        border: none;
      }

      + li {
        margin-left: 0;
      }
    }
  }
}
</style>
